import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Chapter } from 'it/primatoo/openapi/api/org/schemas/common/docs/_listtemplatechapterresponse/Chapter';
import { ChapterStatus } from 'it/primatoo/openapi/api/org/schemas/common/docs/ChapterStatus';
import { Observable } from 'rxjs';
import { CommonDocumentEditorDetails } from '@primatoo-ms-organization';
import { GetChapterResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/GetChapterResponse';
import { ChapterInfo, IoniconsFilled, IoniconsOutline, IT_DATE_FORMAT_WITH_TIME } from '@pt/pt-models';
import { EditorStoreService } from '../services';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ChapterSelector } from 'it/primatoo/openapi/api/org/schemas/common/docs/ChapterSelector';
import { TreatmentService } from '@pt/api-client';
import {
  GetTreatmentLiaRequiredResponse
} from 'it/primatoo/openapi/api/org/schemas/treatment/GetTreatmentLiaRequiredResponse';

@UntilDestroy()
@Component({
  selector: 'pt-editor-chapter-status',
  template: `
    <div class="d-flex flex-column w-100" *ngIf="items.length > 0">
      <div
        class="d-flex flex-row align-items-center"
        *ngIf="!editorReady || !isDraft || !lockAcquired; else ENABLED"
      >
        <div class="d-flex flex-row align-items-center">
          <label>{{ 'LABELS.STATE' | translate }}</label>
          <div class="disabled-status">
            <div class="status-icon" [ngClass]="'bg-' + currentItem.key"></div>
            {{ currentItem?.label | async }}
          </div>
        </div>

        <div
          class="last-change-info"
          *ngIf="chapterInfo?.lastUpdatedAt || chapterInfo?.lastUpdateUser"
        >
          <div class="label">{{ 'LABELS.LAST_CHANGE' | translate }}:</div>
          <div class="date" *ngIf="chapterInfo?.lastUpdatedAt">
            {{
              chapterInfo?.lastUpdatedAt
                | localizedDate : { pattern: IT_DATE_FORMAT_WITH_TIME }
            }}
          </div>
          <div class="name" *ngIf="chapterInfo?.lastUpdateUser">
            ({{ chapterInfo?.lastUpdateUser?.name }}
            {{ chapterInfo?.lastUpdateUser?.surname }})
          </div>
        </div>
      </div>
      <ng-container *ngIf="liaRequired">
        <div class="lia-required">
          <pt-icon
            [key]="IconInfoCircle"
            [size]="16"
            placement="top"
            popoverTitle="Info"
            container="body"
            ngbPopover="{{ liaRequiredMessage }}"
          >
          </pt-icon>
          <span>
            {{ liaRequiredMessage }}
          </span>
        </div>
      </ng-container>
      <ng-template #ENABLED>
        <div class="d-flex flex-row align-items-center">
          <label>
            {{ 'LABELS.STATE' | translate }}
          </label>
          <ng-select
            style="width: 200px; margin-left: 10px;"
            class="d-inline-flex ng-select-25"
            [clearable]="false"
            [searchable]="false"
            [disabled]="!editorReady || !isDraft || !lockAcquired"
            [items]="items"
            (ngModelChange)="changeChapterStatus.emit($event.key)"
            [(ngModel)]="currentItem"
          >
            <ng-template ng-label-tmp let-item="item">
              <div
                class="d-flex flex-row align-items-center"
                style="width: 150px"
              >
                <div class="status-icon" [ngClass]="'bg-' + item.key"></div>
                <div
                  class="status-lock"
                  *ngIf="item.key !== ChapterStatus.WORKING"
                >
                  <pt-icon
                    style="margin-left: -5px"
                    [key]="lock"
                    [size]="14"
                  ></pt-icon>
                </div>
                <span>{{ item.label | async }}</span>
              </div>
            </ng-template>
            <ng-template ng-option-tmp let-item="item" let-index="index">
              <div
                class="d-flex flex-row align-items-center"
                style="width: 150px"
              >
                <div class="status-icon" [ngClass]="'bg-' + item.key"></div>
                <div
                  class="status-lock"
                  *ngIf="item.key !== ChapterStatus.WORKING"
                >
                  <pt-icon
                    style="margin-left: -5px"
                    [key]="lock"
                    [size]="14"
                  ></pt-icon>
                </div>
                <span>{{ item.label | async }}</span>
                <pt-icon
                  *ngIf="item.tooltip"
                  class="text-warning d-flex"
                  container="body"
                  ngbTooltip="{{ item.tooltip }}"
                  [key]="IconWarning"
                >
                </pt-icon>
              </div>
            </ng-template>
          </ng-select>

          <div
            class="last-change-info"
            *ngIf="chapterInfo?.lastUpdatedAt || chapterInfo?.lastUpdateUser"
          >
            <div class="label">{{ 'LABELS.LAST_CHANGE' | translate }}:</div>
            <div class="date" *ngIf="chapterInfo?.lastUpdatedAt">
              {{
                chapterInfo?.lastUpdatedAt
                  | localizedDate : { pattern: IT_DATE_FORMAT_WITH_TIME }
              }}
            </div>
            <div class="name" *ngIf="chapterInfo?.lastUpdateUser">
              ({{ chapterInfo?.lastUpdateUser?.name }}
              {{ chapterInfo?.lastUpdateUser?.surname }})
            </div>
          </div>
        </div>
      </ng-template>

      <div class="alert alert-warning alert-not-aligned" *ngIf="isNotAligned">
        {{ 'MESSAGE.NOT_ALIGNED_DOCUMENT' | translate }}
      </div>
    </div>
  `,
  styles: [
    `

    .lia-required {
      position: relative;
      display: flex;
      flex-direction: row;
      gap: 3px;
      align-items: center;
      justify-content: flex-start;
      margin: 5px 0;

      :is(pt-icon) {
        display: flex;
        color: #2196F3;
        padding-top: 1px;
      }

      :is(span) {
        display: flex;
        font-style: italic;
      }
    }

    .disabled-status {
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;
      display: flex;
      align-items: center;
      border: 0;
      padding: 0 5px;
      margin: 0 5px;
    }

    .bg-WORKING {
      background-color: #ffc107;
    }

    .bg-NOT_STARTED {
      background-color: #bbbec1;
    }

    .bg-TERMINATED {
      background-color: #6bb700;
    }

    .status-lock {
      display: flex;
      align-items: flex-start;
      width: 12px;
      border-radius: 20px;
      font-size: 12px;
    }

    .status-lock pt-icon {
      display: flex;
      align-items: center;

    }

    .status-icon {
      width: 12px;
      height: 12px;
      border-radius: 20px;
      margin-right: 5px;
    }

    .last-change-info {
      display: flex;
      flex-direction: row;
      margin-left: 5px;
    }

    .last-change-info .label {
      margin-left: 5px;
      display: flex;
    }

    .last-change-info .date {
      margin-left: 5px;
      display: flex;
    }

    .last-change-info .name {
      margin-left: 5px;
      display: flex;
    }

    .alert-not-aligned {
      margin: 10px 0 0 0;
      width: 100%;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      padding: 2px 10px
    }
  `,
  ],
})
export class EditorChapterStatusSelectorComponent implements OnChanges {
  @Input() activeChapter: Chapter;
  @Input() lockAcquired: boolean;

  @Input() getChapterResponse: GetChapterResponse;
  @Input() editorReady: boolean;
  @Input() isDraft: boolean;
  @Input() documentEditorDetails: CommonDocumentEditorDetails;
  @Output() changeChapterStatus = new EventEmitter<ChapterStatus>();

  IT_DATE_FORMAT_WITH_TIME = IT_DATE_FORMAT_WITH_TIME;
  items: Array<ChapterStatusItem> = [];
  currentItem?: ChapterStatusItem;
  isNotAligned = false;
  isCompleted = false;
  ChapterStatus = ChapterStatus;

  lock = IoniconsOutline.lock;
  IconInfoCircle = IoniconsFilled.infoCircle;

  chapterInfo?: ChapterInfo;
  liaRequired?: GetTreatmentLiaRequiredResponse;
  liaRequiredMessage: string;
  public IconWarning = IoniconsOutline.warning;

  constructor(
    private translate: TranslateService,
    private treatmentService: TreatmentService,
    private editorStoreService: EditorStoreService
  ) {
    editorStoreService.chapterInfo$
      .pipe(untilDestroyed(this))
      .subscribe((info: ChapterInfo) => {
        this.chapterInfo = info;
      });
  }

  private loadLiaDetails() {
    this.treatmentService
      .liaRequired(this.documentEditorDetails.docId)
      .subscribe((r) => {
        this.liaRequired = r;
        if (r.required) {
          this.liaRequiredMessage =
            'Si richiede la compilazione della LIA, in quanto sussiste il legittimo interesse del titolare.';
          // 'Richiesta la compilazione della lia in quanto presente il legittimo interesse del titolare.';
        } else {
          this.liaRequiredMessage =
            'Non si richiede la compilazione della LIA, in quanto non sussiste il legittimo interesse del titolare.';
        }
      });
  }

  ngOnChanges(): void {
    this.reloadStatus();
  }

  private reloadStatus() {
    if (this.activeChapter) {
      if (this.activeChapter.selector === ChapterSelector.TREAT_LIA) {
        this.loadLiaDetails();
      }
      this.isCompleted = this.activeChapter.completed === 100;
      this.isNotAligned =
        this.activeChapter.currentStatus === ChapterStatus.TERMINATED &&
        !this.isCompleted &&
        this.isDraft;

      this.items = this.activeChapter.supportedStatus
        ? this.activeChapter.supportedStatus.map((c) => {
            if (c === ChapterStatus.TERMINATED && !this.isCompleted) {
              const tooltip: string = "Il 'tab' non è completo";
              const item: ChapterStatusItem = {
                label: this.translate.stream('ENUMS.EDITOR_STATE.' + c),
                key: c,
                disabled: true,
                tooltip: tooltip ?? undefined,
              };
              return item;
            } else {
              const item: ChapterStatusItem = {
                label: this.translate.stream('ENUMS.EDITOR_STATE.' + c),
                key: c,
              };
              return item;
            }
          })
        : [];
      this.currentItem = this.items?.find(
        (c) => c.key === this.activeChapter.currentStatus
      );
    }
  }
}


interface ChapterStatusItem {
  key: ChapterStatus;
  label: Observable<string>;
  disabled?: boolean;
  tooltip?: string;
}
